import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import DataTable from 'react-data-table-component';
import { Spinner, Button } from 'react-bootstrap';
import 'bootstrap-icons/font/bootstrap-icons.css';
import { apiGet, apiPost } from '../services/api';
import { statusConditions } from "./Footer";
import DeadlineHistoryModal from '../components/modals/DeadlineHistoryModal';
import AttachmentsModal from '../components/modals/AttachmentsModal';
import CommentsModal from '../components/modals/CommentsModal';
import { toast } from 'react-toastify';
import { format } from 'date-fns';
import { BsChatText, BsPaperclip } from 'react-icons/bs';

const NotificationDetailTable = () => {
  const { id } = useParams();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [deadlineHistoryModal, setDeadlineHistoryModal] = useState({
    visible: false,
    loading: false,
    deadlines: [],
    currentTaskId: null
  });
  const [attachmentsModal, setAttachmentsModal] = useState({
    show: false,
    taskId: null,
    officerNames: '',
    attachments: [],
    onFileDelete: () => { }
  });
  const [commentsModal, setCommentsModal] = useState({
    show: false,
    taskId: null,
    officerNames: '',
    comments: [],
    existingComments: ''
  });

  const toggleRowExpand = (taskID, field) => {
    setExpandedRows(prev => ({
      ...prev,
      [taskID]: {
        ...prev[taskID],
        [field]: !(prev[taskID]?.[field] || false)
      }
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return format(new Date(dateString), 'dd/MM/yyyy');
    } catch {
      return 'Invalid Date';
    }
  };

  const handleShowDeadlineHistory = async (taskId) => {
    try {
      setDeadlineHistoryModal({ visible: true, loading: true, currentTaskId: taskId, deadlines: [] });
      const response = await apiGet(`/TaskTracking/task-deadlines/${taskId}`);
      if (response?.isSuccess && response.data) {
        const sortedDeadlines = response.data.sort((a, b) => new Date(b.changeDate) - new Date(a.changeDate));
        setDeadlineHistoryModal(prev => ({ ...prev, deadlines: sortedDeadlines, loading: false }));
      } else {
        setDeadlineHistoryModal(prev => ({ ...prev, loading: false }));
        toast.error(response?.message || "No deadline history found");
      }
    } catch (error) {
      console.error("Error fetching deadline history:", error);
      setDeadlineHistoryModal(prev => ({ ...prev, loading: false }));
      toast.error("Failed to load deadline history");
    }
  };

  const handleShowAttachments = async (task) => {
    try {
      const attachments = Array.isArray(task.taskAttachments) ? task.taskAttachments : [];
      setAttachmentsModal({
        show: true,
        taskId: task.taskID,
        officerNames: task.officerNames || 'Officer',
        attachments: attachments,
        onFileDelete: () => {}
      });
    } catch (error) {
      console.error("Error showing attachments:", error);
      toast.error("Failed to load attachments");
    }
  };

  const handleShowOfficerComments = async (task) => {
    try {
      const endpoints = [
        `/Task/GetTaskComments?taskId=${task.taskID}`,
        `/api/TaskComments/${task.taskID}`,
        `/TaskComments/GetByTaskId/${task.taskID}`
      ];
      let response;
      for (const endpoint of endpoints) {
        try {
          response = await apiGet(endpoint);
          if (response) break;
        } catch {}
      }
      const comments = response?.data || [];
      setCommentsModal({
        show: true,
        taskId: task.taskID,
        officerNames: task.officerNames || 'Officer',
        comments: Array.isArray(comments) ? comments : [],
        existingComments: task.additionalComments || 'No comments available'
      });
    } catch (error) {
      toast.error('Could not load comments');
      setCommentsModal({
        show: true,
        taskId: task.taskID,
        officerNames: task.officerNames || 'Officer',
        comments: [],
        existingComments: task.additionalComments || 'No comments available'
      });
    }
  };

  useEffect(() => {
    const fetchNotificationDetails = async () => {
      try {
        setLoading(true);
        const response = await apiPost("/auth/GetNotificationTaskTrackingByTaskID", { NotificationId: id }, true);
        let processedData = Array.isArray(response) ? response : [response];
        
        const tasksWithHistory = await Promise.all(processedData.map(async (task) => {
          try {
            const historyResponse = await apiGet(`/TaskTracking/task-deadlines/${task.taskID}`);
            const hasAttachments = Array.isArray(task.taskAttachments) && task.taskAttachments.length > 0;
            return {
              ...task,
              hasDeadlineHistory: historyResponse?.data?.length > 0,
              hasAttachments: hasAttachments,
              taskAttachments: hasAttachments ? task.taskAttachments : [],
              hasComments: task.additionalComments && task.additionalComments !== 'N/A'
            };
          } catch (error) {
            console.error(`Error checking history for task ${task.taskID}:`, error);
            const hasAttachments = Array.isArray(task.taskAttachments) && task.taskAttachments.length > 0;
            return {
              ...task,
              hasDeadlineHistory: false,
              hasAttachments: hasAttachments,
              taskAttachments: hasAttachments ? task.taskAttachments : [],
              hasComments: task.additionalComments && task.additionalComments !== 'N/A'
            };
          }
        }));
        
        setNotifications(tasksWithHistory);
      } catch (err) {
        setError(err.message || 'Failed to fetch notification details');
        setNotifications([]);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchNotificationDetails();
  }, [id]);

  const columns = [
    {
      name: "Task ID",
      selector: (row) => row.taskID,
      sortable: true,
            width: '140px',
   center:true,
    style: {
      borderRight: '1px solid #dee2e6',
    },
    },
    {
      name: "Issue/Action/Decision",
      selector: (row) => row.issueActionOrDecision,
      sortable: true,
      wrap: true,
    cell: (row) => {
        const text = row.issueActionOrDecision || "";
        const isExpanded = expandedRows[row.taskID]?.issueExpanded || false;
        const showToggle = text.length > 100;
        return (
          <div>
            <div style={{ whiteSpace: "pre-wrap", textAlign:"left" }}>
              {isExpanded ? text : text.slice(0, 100)}
              {text.length > 100 && !isExpanded && "..."}
            </div>
            {showToggle && (
              <Button variant="link" size="sm"  className="p-0 ms-1 view-more"
                onClick={(e) => {
                  e.stopPropagation();
                  toggleRowExpand(row.taskID, 'issueExpanded');
                }}
              >
                {isExpanded ? "View Less" : "View More"}
              </Button>
            )}
          </div>
        );
      },
            width: '240px',
   center:true,
    style: {
      borderRight: '1px solid #dee2e6',
    },
    },
    {
      name: 'Priority',
      selector: row => row.priority,
      cell: (row) => {
        const priority = (row.priority || '').toLowerCase();
        let bg = "transparent", color = "#1e293b", label = row.priority || "N/A";
        switch (priority) {
          case "top":  color = "#92400e"; break;
          case "high":  color = "#7f1d1d"; break;
          case "medium":  color = "#1e3a8a"; break;
          case "low":  color = "black"; break;
          default: label = "N/A";
        }
        return (
          <span style={{
            display: "inline-block",
            padding: "4px 12px",
            borderRadius: "999px",
            backgroundColor: bg,
            color,
            fontWeight: "600",
            fontSize: "13px",
            textTransform: "capitalize",
          }}>
            {label}
          </span>
        );
      },
      sortable: true,
     width: '150px',
   center:true,
    style: {
      borderRight: '1px solid #dee2e6',
    },
    },
    {
      name: 'Decision Date',
      selector: row => formatDate(row.dateOfDecisionOrInitiation),
      sortable: true,
            width: '170px',
   center:true,
    style: {
      borderRight: '1px solid #dee2e6',
    },
    },
    {
      name: "Deadline",
      selector: (row) => formatDate(row.deadline),
      cell: (row) => (
        <div className="d-flex align-items-center">
          <span>{formatDate(row.deadline)}</span>
          {row.hasDeadlineHistory && (
            <i 
              className="bi bi-clock-history ms-2 text-muted cursor-pointer mediumcolor"
              onClick={(e) => {
                e.stopPropagation();
                handleShowDeadlineHistory(row.taskID);
              }}
              title="View deadline history"
              style={{ fontSize: '1.2rem' }}
            />
          )}
        </div>
      ),
      sortable: true,
      width: '150px',
   center:true,
    style: {
      borderRight: '1px solid #dee2e6',
    },
    },
    {
      name: 'Coordinators',
      selector: row => row.coordinatorNames || '-',
       width: '180px',
   center:true,
    style: {
      borderRight: '1px solid #dee2e6',
    },
    },
    {
      name: 'Action Taken / Status',
      selector: row => row.actionTakenOrStatus || '-',
      sortable: true,
       width: '240px',
   center:true,
    style: {
      borderRight: '1px solid #dee2e6',
    },
    },
    {
      name: 'Officer Responsible',
      cell: row => (
        <div className="d-flex align-items-center">
          <span>{row.officerNames || 'N/A'}</span>
          {row.hasComments && (
            <BsChatText className="ms-2 text-muted cursor-pointer mediumcolor"
              onClick={(e) => {
                e.stopPropagation();
                handleShowOfficerComments(row);
              }}
              title="View comments"
              style={{ fontSize: '1.2rem' }}
            />
          )}
          {(row.hasAttachments || row.taskAttachments?.length > 0) && (
            <BsPaperclip className="ms-2 text-muted cursor-pointer mediumcolor"
              onClick={(e) => {
                e.stopPropagation();
                handleShowAttachments(row);
              }}
              title="View attachments"
              style={{ fontSize: '1.2rem' }}
            />
          )}
        </div>
      ),
      wrap: true,
       width: '220px',
   center:true,
    style: {
      borderRight: '1px solid #dee2e6',
    },
    },
    {
      name: 'Additional Comments',
      selector: row => row.additionalComments || '-',
      sortable: true,
       width: '240px',
   center:true,
    style: {
      borderRight: '1px solid #dee2e6',
    },
    },
    {
      name: "Status",
      selector: row => row.status,
      cell: row => (
        <img
          style={{ maxWidth: 60, maxHeight: 60 }}
          src={`${process.env.PUBLIC_URL}/icons/${
            row.status === "Significant Delay" ? statusConditions[4].icon :
            row.status === "Slight Delay" ? statusConditions[3].icon :
            row.status === "On Time" ? statusConditions[2].icon :
            row.status === "Completed" ? statusConditions[0].icon :
            statusConditions[1].icon
          }`}
          alt="status"
        />
      ),
      sortable: true,
       width: '110px',
   center:true,
    
    }
  ];

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '300px' }}>
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (error) {
    return <div className="alert alert-danger m-3">Error: {error}</div>;
  }

  return (
    <div className="main-content">
      <div className="container">
        <div className="card shadow-sm rounded-4 py-4 px-0 border-0 bg-white mb-custom">
          <div className="card-header bg-white border-0 px-0">
            <h4 className="mb-2">Notification Details</h4>
          </div>
          <DataTable
            columns={columns}
           
              data={notifications.map((dept, idx) => ({
    ...dept,
    _rowIndex: idx, 
  }))}
            pagination
            striped
             responsive
                className="p-datatable-striped customuser-data-table customarchive-data-table   dashboard-table"
      customStyles={{
              table: {
                style: {
             
      fontWeight: '600',
      fontSize: '14px',
      backgroundColor: '#f8f9fa',
      textAlign: 'center',
                },
              },
              headRow: {
                style: {
                  backgroundColor: "#04364A!important",
                  minHeight: "48px",
                },
              },
              headCells: {
                style: {
                  padding: "10px 14px",
                  fontSize: "15px",
                  fontWeight: "700",
                  color: "white",
                  textTransform: "uppercase",
                   textAlign: columns.name === "Issue/Action/Decision" ? "center" : "left",
                },
              },
           
              cells: {
                style: {
                  padding: "10px 14px",
                  fontSize: "14px",
                  color: "#334155",
                },
              },
            }}
      conditionalRowStyles={[
     {
    when: (row) => row._rowIndex % 2 === 0, 
    style: { backgroundColor: '#f2f2f2' },
  },
  {
    when: (row) => row._rowIndex % 2 !== 0,
    style: { backgroundColor: '#ffffff' },
  },
  ]}  
         
          />
        </div>
      </div>

      <DeadlineHistoryModal
        show={deadlineHistoryModal.visible}
        onHide={() => setDeadlineHistoryModal(prev => ({ ...prev, visible: false }))}
        deadlines={deadlineHistoryModal.deadlines}
        loading={deadlineHistoryModal.loading}
        taskId={deadlineHistoryModal.currentTaskId}
      />

      <AttachmentsModal
        show={attachmentsModal.show}
        onHide={() => setAttachmentsModal(prev => ({ ...prev, show: false }))}
        taskId={attachmentsModal.taskId}
        officerNames={attachmentsModal.officerNames}
        attachments={attachmentsModal.attachments}
        onFileDelete={attachmentsModal.onFileDelete}
      />

      <CommentsModal
        show={commentsModal.show}
        onHide={() => setCommentsModal(prev => ({ ...prev, show: false }))}
        taskId={commentsModal.taskId}
        officerNames={commentsModal.officerNames}
        comments={commentsModal.comments}
        existingComments={commentsModal.existingComments}
      />
    </div>
  );
};

export default NotificationDetailTable;